import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { WatchVideoService } from "src/app/services/watch-video-service/watch-video.service";
import { AuthService } from "src/app/pages/auth";
import videojs from "video.js";
import "videojs-overlay";
// Space / ArrowLeft / ArrowRight while the player has focus, same keys eazycal
// wires by hand.
import "videojs-hotkeys";
import Player from "video.js/dist/types/player";

/**
 * ±10s buttons in the control bar.
 *
 * eazycal gets these from `videojs-seek-buttons`, which targets video.js 7 —
 * this player is on 8. Two Button subclasses cost less than a pinned
 * incompatible dependency, and video.js 8 already ships the two icons.
 */
function registerSeekButtons(): void {
  const vjs = videojs as any;
  if (vjs.getComponent("SeekBack10")) {
    return;
  }
  const Button = vjs.getComponent("Button");

  class SeekBack10 extends Button {
    constructor(player: any, options: any) {
      super(player, options);
      this.controlText("ถอยหลัง 10 วินาที");
      this.addClass("vjs-icon-replay-10");
    }
    handleClick() {
      const p = (this as any).player();
      p.currentTime(Math.max(0, p.currentTime() - 10));
    }
  }

  class SeekForward10 extends Button {
    constructor(player: any, options: any) {
      super(player, options);
      this.controlText("เดินหน้า 10 วินาที");
      this.addClass("vjs-icon-forward-10");
    }
    handleClick() {
      const p = (this as any).player();
      const target = p.currentTime() + 10;
      const duration = p.duration();
      // `duration()` is NaN until the manifest is parsed and 0 for a stream of
      // unknown length; clamping against either sends the viewer to the start.
      p.currentTime(
        Number.isFinite(duration) && duration > 0
          ? Math.min(duration, target)
          : target,
      );
    }
  }

  vjs.registerComponent("SeekBack10", SeekBack10);
  vjs.registerComponent("SeekForward10", SeekForward10);
}

@Component({
  selector: "app-video-player",
  imports: [],
  templateUrl: "./video-player.component.html",
  styleUrl: "./video-player.component.scss",
})
export class VideoPlayerComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild("target", { static: true }) target!: ElementRef;

  player!: Player;
  @Input() playlist: any;
  @Input() video: { src: string; time: number };
  @Input() chapter_id!: string | number | null;

  @Input()
  currentChapter!: { url: string; time: number };

  @Output()
  updateCurrentVideo = new EventEmitter<number>();
  @Output()
  videoListener = new EventEmitter<{ is_playing: boolean }>();
  /** The clip played to the end — the parent decides what comes next. */
  @Output()
  videoEnded = new EventEmitter<void>();

  watchVideoService = inject(WatchVideoService);
  #auth = inject(AuthService);

  /** Set when the parent switched chapter because the previous one ended, so
   *  the next clip starts on its own instead of waiting for another click. */
  private autoplayOnLoad = false;

  private lastSavedTime = 0;
  private retryCount = 0;
  private MAX_RETRY = 2;

  // Keep the HLS ticket cookie alive while watching (API TTL ~10 min); renew
  // well before expiry so playback never trips a 410. Mirrors eazycal's watch
  // player.
  private accessRenewTimer: any = null;
  private readonly ACCESS_RENEW_MS = 5 * 60 * 1000;

  ngOnInit(): void {
    this.initVideoPlayer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["currentChapter"] &&
      this.player &&
      this.currentChapter?.url &&
      changes["currentChapter"].previousValue?.url !==
      changes["currentChapter"].currentValue?.url
    ) {
      this.loadVideo();
    }
  }

  initVideoPlayer() {
    registerSeekButtons();
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const agent = this.getMobileOperatingSystem();

    this.player = videojs(this.target.nativeElement, {
      controls: true,
      // Not `fluid: true`. Fluid mode sets its own inline `padding-top`
      // percentage on `.video-js`, computed from the LOADED video's own
      // dimensions — for a portrait mobile recording that pushes the player
      // taller than `.video-container`'s fixed `aspect-ratio: 16/9` box, and
      // `overflow: hidden` on the container then clips whatever spills past.
      // The container is already responsive on its own CSS (`width: 100%` +
      // `aspect-ratio`), and `object-fit: contain` on the `<video>` itself
      // already letterboxes any ratio without cropping, so fluid mode has
      // nothing to add here — only a conflicting size to fight over.
      fluid: false,
      preload: "metadata",
      autoplay: false,
      muted: true, // important!! mobile autoplay | if false video won't resume time progress
      playsinline: true, // prevent iOS auto fullscreen
      fullscreen: {
        options: { navigationUI: "hide" }, // ลด native behavior
      },
      html5: {
        nativeAudioTracks: false,
        nativeVideoTracks: false,

        vhs: {
          overrideNative: !isSafari,
          enableLowInitialPlaylist: true,
          maxBufferLength: 60,
        },
      },
      // Same ladder as eazycal — 0.5x is not a speed anyone revises at, and
      // 1.2x is the one people actually use.
      playbackRates: [1, 1.2, 1.5, 1.75, 2],
    });

    const controlBar = (this.player as any).controlBar;
    controlBar.addChild("SeekBack10", {}, 1);
    controlBar.addChild("SeekForward10", {}, 2);

    (this.player as any).hotkeys?.({
      volumeStep: 0.1,
      seekStep: 10,
      enableModifiersForNumbers: false,
      enableVolumeScroll: false,
    });

    // Right-click offers "Save video as…" on some builds. It cannot stop a
    // determined copy, but it should not be one click away either.
    this.player.on("contextmenu", (e: any) => e.preventDefault());

    // Auto-advance: eazycal moves to the next clip when one finishes. The
    // parent owns the playlist (and knows which chapters a trial locks), so
    // just say it ended.
    this.player.on("ended", () => {
      this.autoplayOnLoad = true;
      this.videoEnded.emit();
    });

    this.player.ready(() => {
      this.applyWatermark();
      const videoEl = this.player.el().querySelector("video");
      if (agent === "iOS") {
        videoEl?.addEventListener("play", () => {
          this.onPlayListener();
        });
        videoEl?.addEventListener("pause", () => {
          this.onPauseListener();
        });
      }
    });

    this.player.one("playing", () => {
      // this.onPlayListener();
      this.watchVideoService.videoLoading.update(() => false);
    });
    this.player.one("pause", () => {
      this.onPauseListener();
    });
    this.player.one("play", () => {
      this.onPlayListener();
    });

    this.player.on("error", async () => {
      // console.log("player error");

      const err = this.player.error();
      if (err?.code === 4 && this.retryCount < this.MAX_RETRY) {
        this.retryCount++;
        await this.loadVideo();
      }
    });

    if (this.currentChapter?.url) {
      this.loadVideo();
    } else {
      this.watchVideoService.videoLoading.update(() => false);
    }
  }

  getMobileOperatingSystem(): string {
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIPad =
      navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;

    if (/iPad|iPhone|iPod/.test(userAgent) || isIPad) {
      return "iOS";
    }
    // if (/android/i.test(userAgent)) {
    //   return 'Android';
    // }
    return "unknown";
  }

  async loadVideo() {
    this.lastSavedTime = 0;
    if (!this.currentChapter?.url) return;
    if (!this.player || this.player.isDisposed()) return;

    try {
      this.watchVideoService.videoLoading.update(() => true);
      this.applyWatermark();
      // Cookie-based (secure_link) — issue the HttpOnly ticket, then play the
      // clean URL; nginx verifies the cookie on every segment. No token query.
      await this.watchVideoService
        .requestHlsAccess(+this.chapter_id!)
        .toPromise();
      (videojs as any).Vhs.xhr.beforeRequest = null;
      this.startAccessRenew();
      const videoUrl = this.currentChapter.url;
      const resume = Number(this.currentChapter?.time) || 0;

      this.player.pause();

      // reset old event
      this.player.off("loadedmetadata");
      this.player.off("canplay");

      // trick for mobile HLS
      this.player.one("loadedmetadata", () => {
        const trySeek = () => {
          if (this.player.readyState() >= 1) {
            // 🔥 trigger to load segment
            this.player!.play()!
              .then(() => {
                this.player.pause();
                this.onResumeTime(resume);
              })
              .catch(() => {
                this.onResumeTime(resume);
                // setTimeout(() => {
                //   this.watchVideoService.videoLoading.update(() => false);
                // }, 200);
              });
          } else {
            setTimeout(trySeek, 100);
            this.watchVideoService.videoLoading.update(() => false);
          }
        };

        trySeek();
      });

      this.player.one("canplay", () => {
        setTimeout(() => {
          this.watchVideoService.videoLoading.update(() => false);
        }, 200);
      });

      // set source
      this.player.src({
        src: videoUrl,
        type: "application/x-mpegURL",
      });

      // important for mobile refresh
      this.player.load();
      this.player.pause();
    } catch (err) {
      console.error("video load error", err);
      this.watchVideoService.videoLoading.update(() => false);
    }
  }

  /**
   * Renews the HLS ticket cookie on an interval so a long watch never trips a
   * 410. A transient renew failure is harmless — the next segment 403 surfaces
   * as a player error (code 4) and loadVideo() re-issues a fresh ticket.
   */
  private startAccessRenew(): void {
    this.clearAccessRenew();
    this.accessRenewTimer = setInterval(() => {
      if (this.chapter_id == null) return;
      this.watchVideoService
        .requestHlsAccess(+this.chapter_id)
        .toPromise()
        .catch((err) => console.log("hls ticket renew failed", err));
    }, this.ACCESS_RENEW_MS);
  }

  private clearAccessRenew(): void {
    if (this.accessRenewTimer) {
      clearInterval(this.accessRenewTimer);
      this.accessRenewTimer = null;
    }
  }

  /**
   * Stamp the viewer's own name over the picture.
   *
   * The point is that a screen recording carries the account that made it.
   * eazycal uses `videojs-watermark-with-text`, which calls the `videojs.plugin`
   * registration removed in video.js 8 — importing it here throws — and it fades
   * the mark out three seconds after play, which protects nothing. So: the same
   * mark, built directly, and it stays.
   *
   * Appended to the player root rather than the template so it survives
   * fullscreen; styled inline because it is created outside Angular's view
   * encapsulation and would otherwise get no styles at all.
   */
  private applyWatermark(): void {
    const auth = this.#auth.currentUser() as any;
    const name = auth?.user?.data?.full_name || auth?.user?.email;
    if (!name) {
      return;
    }
    const root = this.player.el() as HTMLElement;
    if (root.querySelector(".vjs-student-watermark")) {
      return;
    }
    const mark = document.createElement("div");
    mark.className = "vjs-student-watermark";
    mark.textContent = name;
    mark.style.cssText = [
      "position:absolute",
      "top:12px",
      "right:14px",
      "z-index:10",
      "pointer-events:none",
      "user-select:none",
      "color:#fff",
      "opacity:.55",
      "font-size:14px",
      "font-weight:600",
      "text-shadow:0 0 6px rgba(0,0,0,.9)",
    ].join(";");
    root.appendChild(mark);
  }

  pause() {
    this.player.pause();
  }

  play() {
    this.player.play();
  }

  disposeVideo() {
    if (this.player && !this.player.isDisposed()) {
      (this.player as any).off(); // กัน memory leak
      this.player.dispose();
    }
  }

  onPause() {
    this.player?.pause()!;
  }
  onResume() {
    this.player?.play()!;
  }
  onResumeTime(resume: number) {
    if (resume > 0) {
      this.player.currentTime(resume);
    }
    // Auto-advance keeps playing. This runs at the END of loadVideo's warm-up
    // (which deliberately plays then pauses to force the first segments down);
    // starting any earlier just gets cancelled by that pause.
    if (this.autoplayOnLoad) {
      this.autoplayOnLoad = false;
      // The player is muted, so autoplay policy does not block this.
      this.player.play()?.catch(() => {});
    }
  }

  onTrigger() {
    this.player?.play()!.then(() => {
      this.player.pause();
    });
  }

  getCurrentTime(): number {
    return this.player.currentTime()!;
  }
  getIsPlaying(): boolean {
    return !this.player.paused()!;
  }
  getIsDisposed(): boolean {
    return this.player.isDisposed()!;
  }

  onPlayListener() {
    // console.log("play");
    this.videoListener.emit({ is_playing: true });
  }

  onPauseListener() {
    // console.log("paused");
    this.videoListener.emit({ is_playing: false });
  }

  ngOnDestroy(): void {
    this.clearAccessRenew();
    this.disposeVideo();
  }
}
